import { useState, useCallback, useReducer, useEffect } from 'react';

import {
  FormControl,
  IconButton,
  Paper,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  CircularProgress,
  Drawer,
} from '@mui/material';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import { analyzerChanged } from '../analyzers/analyzerSlice';

import {
  getTraceHistoryByPage,
  getTraceHistoryCount,
} from '../../api/snapshotApi';

import { useAppDispatch } from '../../app/hooks';
import { ActiveSettingsKey, HistoryState, TraceHistoryAction } from './types';

import TraceHistoryToolbar from './TraceHistoryToolbar';
import TraceHistoryPlayBack from './TraceHistoryPlayback';
import TraceHistoryPlayBackControls from './TraceHistoryPlaybackControls';
import TraceHistoryFilter from './TraceHistoryFilter';
import SettingsTabPanel from '../singleTraceView/SettingsTabPanel';
import MarkerHistory from '../markerHistory/MarkerHistory';
import MonitorConfigurationDialog from '../monitorConfiguration/MonitorConfigurationDialog';
import SpectralMaskConfigurationDialog from '../spectralMaskConfiguration/SpectralMaskConfigurationDialog';
import ThresholdConfigurationDialog from '../thresholds/ThresholdConfigurationDialog';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import ExportHistoryButton from './ExportHistoryButton';
import { InterferenceDetector } from 'types/shared/InterferenceDetector';

import ShareLinkDialog from './ShareLinkDialog';
import { ChevronRight, ChevronLeft } from '@mui/icons-material';

import {
  traceHistoryReducer,
  initialHistoryState,
} from './utils/traceHistory.reducer';

import { useAPICall } from 'hooks/useAPICall';
import { useGetPlaybackWidth } from './hooks/useGetPlaybackWidth';
import { useParseQueryString } from './hooks/useParseQueryString';
import usePlaybackBar from './hooks/usePlaybackBar';
import useTraceHistoryToolbar from './hooks/useTraceHistoryToolbar';
import useLoadHistoryLink from './hooks/useLoadHistoryLink';
import useResizeDrawer from './hooks/useResizeDrawer';
import { useGetFilterState } from './hooks/useGetFilterState';
import { useSystemModules } from 'features/systemModules/api/getSystemModules';
import { zonedTimeToUtc } from 'date-fns-tz';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { debounce } from 'features/spectrumChart/utils/debounce';

const TraceHistoryPage = () => {
  const dispatch = useAppDispatch();
  const { timezone } = useUserDateTime();
  const { data: modules } = useSystemModules({});
  const {
    control: control1,
    watch: watch1,
    getValues: getValues1,
    setValue: setValue1,
    saveAnalyzerAndSnapshotIds: save1,
    errors: errors1,
  } = useGetFilterState(1);
  const {
    control: control2,
    watch: watch2,
    getValues: getValues2,
    errors: errors2,
  } = useGetFilterState(2);

  const [drawingMode, setDrawingMode] = useState<{
    isDrawing: boolean;
    drawingType: InterferenceDetector;
    redrawId: number;
    name?: string | undefined;
  }>({
    isDrawing: false,
    drawingType: InterferenceDetector.Monitor,
    redrawId: -1,
    name: undefined,
  });

  const handleDrawingChange = (newDrawingMode: any) => {
    setDrawingMode(newDrawingMode);
  };

  const [traceHistoryState, dispatchTraceHistory] = useReducer<
    (state: HistoryState, action: TraceHistoryAction) => HistoryState
  >(traceHistoryReducer, initialHistoryState);

  const {
    traceHistory,
    traceCount,
    noDataFlag,
    isHistoryLoading,
    multiHistory,
    showMarkerHistory,
    smallestTraceHistoryLength,
    graphSize,
    hideDetectorNames,
  } = traceHistoryState || initialHistoryState;

  const [globalTraceIndex, setGlobalTraceIndex] = useState(0);
  const [localTraceIndex, setLocalTraceIndex] = useState<{
    1: number;
    2: number;
  }>({
    1: 0,
    2: 0,
  });
  const [currentHoveredSnapshotKey, setCurrentHoveredSnapshotKey] = useState<
    1 | 2
  >(1);

  const [activeSettingsKey, setActiveSettingsKey] =
    useState<ActiveSettingsKey>(1);

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const [hideFilters, setHideFilters] = useState<boolean>(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const activeHistory = activeSettingsKey === 1 ? getValues1() : getValues2();

  const {
    handlePlayClicked,
    handlePauseClicked,
    handleForwardClicked,
    handleBackwardClicked,
    playTimerInterval,
    setPlayTimerInterval,
    playTimerStatus,
  } = usePlaybackBar({
    traceHistory,
    globalTraceIndex,
    smallestTraceHistoryLength,
    setGlobalTraceIndex,
    setLocalTraceIndex,
  });

  const {
    currentDialog,
    clearDetector,
    openMonitorDialog,
    openMaskDialog,
    openThresholdDialog,
    closeDialogs,
    handleBaselineClicked,
    handleCreateMonitorClicked,
    handleCreateMaskClicked,
    handleCreateThresholdClicked,
    handleRedrawMonitor,
    handleRedrawMask,
    handleRedrawThreshold,
    handleClearClicked,
    clearAlarmTriggers,
  } = useTraceHistoryToolbar({
    traceHistory,
    activeHistory,
    currentHoveredSnapshotKey,
    globalTraceIndex,
    activeSettingsKey,
    setDeleteDialogOpen,
  });

  // Set parameters and load data based on query parameters, if any
  const {
    hasQueryString,
    startTime,
    endTime,
    snapshotIdToLoad,
    analyzerIdToLoad,
    traceIndex,
  } = useParseQueryString(!!traceHistory);

  const handleSubmit = async () => {
    dispatchTraceHistory({ type: 'RESET-STATE' });
    await fetchHistoryCount();
    dispatchTraceHistory({ type: 'LOAD-HISTORY' });
    setLocalTraceIndex({ 1: 0, 2: 0 });
  };

  useLoadHistoryLink({
    hasQueryString,
    snapshotIdToLoad,
    analyzerIdToLoad,
    setLocalTraceIndex,
    setGlobalTraceIndex,
    traceIndex,
    startTime,
    endTime,
    setValue: setValue1,
    handleSubmit,
  });

  const { safeCall: safeGetTraceHistory } = useAPICall(getTraceHistoryByPage);

  const { drawerHeight, drawerOpen, drawerContainerRef, setDrawerOpen } =
    useResizeDrawer({
      isHistoryLoading,
      hideFilters,
    });

  const playbackWidth = useGetPlaybackWidth(
    drawerOpen,
    !hideFilters,
    multiHistory
  );

  const handleLocalTraceIndexChanged = (
    historyIndex: number,
    newValue: number | number[]
  ) => {
    if (Array.isArray(newValue)) return;

    setLocalTraceIndex({
      ...localTraceIndex,
      [historyIndex]: newValue,
    });
  };

  const handleTraceIndexChange = (event: any, newValue: number | number[]) => {
    if (typeof newValue === 'number') {
      setGlobalTraceIndex(newValue);
    }
  };

  const debouncedFetchTraceHistory = debounce(() => fetchTraceHistory(), 50);

  useEffect(() => {
    const formOne = getValues1();
    if (formOne.snapshotId === -1) return;
    debouncedFetchTraceHistory();
    //eslint-disable-next-line
  }, [localTraceIndex]);

  // const toggleMarkerHistory = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   dispatchTraceHistory({ type: 'TOGGLE-MARKER-HISTORY' });
  // };

  const toggleDetectorNames = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatchTraceHistory({ type: 'TOGGLE-HIDE-DETECTOR-NAMES' });
  };

  const fetchTraceHistory = async () => {
    const formOne = getValues1();
    const historyOne = await safeGetTraceHistory(
      formOne.snapshotId,
      zonedTimeToUtc(formOne.fromDate, timezone).toISOString(),
      zonedTimeToUtc(formOne.toDate, timezone).toISOString(),
      localTraceIndex[1]
    );
    save1();

    dispatchTraceHistory({
      type: 'HISTORY-LOADED',
      payload: {
        historyData: {
          historyOne,
          historyTwo: [],
        },
      },
    });

    dispatch(analyzerChanged(formOne.analyzerId));
  };

  const fetchHistoryCount = async () => {
    const formOne = getValues1();
    const historyCount = await getTraceHistoryCount(
      formOne.snapshotId,
      zonedTimeToUtc(formOne.fromDate, timezone).toISOString(),
      zonedTimeToUtc(formOne.toDate, timezone).toISOString()
    );

    dispatchTraceHistory({
      type: 'HISTORY-COUNT-LOADED',
      payload: historyCount,
    });
  };

  const moveTraceIndexById = useCallback(
    (id: number) => {
      if (!traceHistory) return;
      const foundIndex = traceHistory[currentHoveredSnapshotKey]?.findIndex(
        (history: any) => history.id === id
      );
      if (foundIndex !== undefined && foundIndex > -1) {
        setGlobalTraceIndex(foundIndex);
      }
    },
    [currentHoveredSnapshotKey, traceHistory]
  );

  const onSnapshotHover = (key: 1 | 2) => {
    setCurrentHoveredSnapshotKey(key);
  };

  const handleActiveSettingsKeyChanged = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setActiveSettingsKey(Number(event.target.value) as ActiveSettingsKey);
  };

  const handleShareHistory = () => {
    if (typeof window !== 'undefined') {
      const protocol = window.location.protocol;
      const host = window.location.host;
      const selectedFilters = getValues1();
      const urlString = `${protocol}//${host}/trace-history?start=${zonedTimeToUtc(
        selectedFilters.fromDate,
        timezone
      ).toISOString()}&end=${zonedTimeToUtc(
        selectedFilters.toDate,
        timezone
      ).toISOString()}&analyzer=${selectedFilters.analyzerId}&snapshot=${
        selectedFilters.snapshotId
      }&trace=${localTraceIndex[1] + 1}`;
      setLinkUrl(urlString);
      setLinkDialogOpen(true);
    }
  };

  return (
    <>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        itemToDelete={clearDetector}
        onSubmit={clearAlarmTriggers}
        onClose={() => setDeleteDialogOpen(false)}
      />
      {traceHistory && (
        <>
          <MonitorConfigurationDialog
            traceLength={601}
            open={currentDialog === 'monitor'}
            handleClose={closeDialogs}
            handleRedrawMonitor={handleRedrawMonitor}
            selectedSnapshotId={
              traceHistory[currentHoveredSnapshotKey][globalTraceIndex]
                ?.snapshot_id
            }
            isHistoryMode
          />
          <SpectralMaskConfigurationDialog
            traceLength={601}
            open={currentDialog === 'spectral-mask'}
            handleClose={closeDialogs}
            handleRedrawMask={handleRedrawMask}
            selectedSnapshotId={
              traceHistory[currentHoveredSnapshotKey][globalTraceIndex]
                ?.snapshot_id
            }
            isHistoryMode
          />
          <ThresholdConfigurationDialog
            traceLength={601}
            open={currentDialog === 'threshold'}
            handleClose={closeDialogs}
            handleRedrawThreshold={handleRedrawThreshold}
            selectedSnapshotId={
              traceHistory[currentHoveredSnapshotKey][globalTraceIndex]
                ?.snapshot_id
            }
            isHistoryMode
          />
        </>
      )}
      <Grid
        container
        spacing={1}
        direction='column'
        style={{ height: 'calc(93vh - 64px)', position: 'relative' }}
        wrap='nowrap'
      >
        <IconButton
          sx={{
            position: 'absolute',
            right: 10,
            top: 20,
          }}
          onClick={() => setHideFilters(!hideFilters)}
        >
          {!hideFilters ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
        {!hideFilters && (
          <Grid item container spacing={1} wrap='nowrap'>
            <Grid container item xs={8} spacing={2} direction='column'>
              <Grid container item spacing={1}>
                {!hideFilters && (
                  <TraceHistoryFilter watch={watch1} control={control1} />
                )}
              </Grid>
              <Grid container item spacing={1}>
                {!hideFilters && modules?.ADVANCED_HISTORY && (
                  <TraceHistoryFilter watch={watch2} control={control2} />
                )}
              </Grid>
            </Grid>

            <Grid
              container
              item
              xs
              spacing={1}
              alignItems='center'
              justifyContent='flex-end'
              wrap='nowrap'
              style={{ maxHeight: '76px' }}
            >
              <Grid item style={{ minWidth: '9rem' }}>
                <ExportHistoryButton
                  snapshotId={getValues1().snapshotId}
                  begin={getValues1().fromDate}
                  end={getValues1().toDate}
                  disabled={!traceHistory || traceCount[1] > 10000}
                />
              </Grid>
              <Grid item style={{ minWidth: '9rem' }}>
                {!hideFilters && (
                  <Button
                    onClick={handleShareHistory}
                    variant='contained'
                    color='primary'
                    fullWidth
                    disabled={!traceHistory || traceHistory[1].length === 0}
                  >
                    Share History
                  </Button>
                )}
              </Grid>
              <Grid item style={{ minWidth: '9rem' }}>
                {!hideFilters && (
                  <Button
                    onClick={handleSubmit}
                    variant='contained'
                    color='primary'
                    fullWidth
                    disabled={
                      watch1('snapshotId') < 0 || watch1('analyzerId') < 0
                    }
                  >
                    Get History
                  </Button>
                )}
              </Grid>
              <Grid
                item
                style={{ height: '48px', width: '48px', margin: 12 }}
              />
            </Grid>
          </Grid>
        )}
        <Grid item container justifyContent='center' alignContent='center'>
          {(!!errors1.fromDate || !!errors2.fromDate) && (
            <h2 style={{ color: 'red' }}>To Date is before From Date</h2>
          )}
          {noDataFlag && (
            <h2 style={{ color: 'red' }}>No Trace History Found</h2>
          )}
          {isHistoryLoading && (
            <Grid item style={{ marginTop: '30vh' }}>
              <CircularProgress />
            </Grid>
          )}
        </Grid>
        {!noDataFlag && traceHistory && (
          <Grid container item style={{ height: '100%' }}>
            <Grid
              container
              item
              xs={12}
              component={Paper}
              direction='column'
              style={{ paddingBottom: '.2rem', height: '100%' }}
            >
              <Grid container item alignItems='center'>
                {modules?.ADVANCED_HISTORY && (
                  <TraceHistoryToolbar
                    snapshotId={activeHistory.snapshotId}
                    analyzer={undefined}
                    openMonitorDialog={openMonitorDialog}
                    openThresholdDialog={openThresholdDialog}
                    openMaskDialog={openMaskDialog}
                    handleBaselineClicked={handleBaselineClicked}
                    handleCreateMonitorClicked={handleCreateMonitorClicked}
                    handleCreateThresholdClicked={handleCreateThresholdClicked}
                    handleCreateMaskClicked={handleCreateMaskClicked}
                    handleClearClicked={handleClearClicked}
                  />
                )}
                <Grid item>
                  {multiHistory && (
                    <FormControl component='fieldset'>
                      <RadioGroup
                        aria-label='Snapshots'
                        value={activeSettingsKey}
                        onChange={handleActiveSettingsKeyChanged}
                        row
                      >
                        <FormControlLabel
                          value={1}
                          control={<Radio />}
                          label='Snapshot 1'
                        />
                        <FormControlLabel
                          value={2}
                          control={<Radio />}
                          label='Snapshot 2'
                        />
                      </RadioGroup>
                    </FormControl>
                  )}
                </Grid>
                {/* <Grid item style={{ paddingLeft: '.5rem' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showMarkerHistory}
                        onChange={toggleMarkerHistory}
                      />
                    }
                    label='Show Marker History'
                  />
                </Grid> */}
                <Grid
                  item
                  style={{ paddingLeft: '.5rem', paddingRight: '3rem' }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={hideDetectorNames}
                        onChange={toggleDetectorNames}
                      />
                    }
                    label='Hide Detector Names'
                  />
                </Grid>
              </Grid>
              <Grid item container xs wrap='nowrap' style={{ height: '100%' }}>
                {!drawerOpen && <Grid item xs />}
                <Grid
                  item
                  xs={playbackWidth}
                  container
                  direction='column'
                  style={{ height: '100%', width: '100%' }}
                  ref={drawerContainerRef}
                  wrap='nowrap'
                  spacing={1}
                >
                  <Grid container item justifyContent='center'>
                    {traceHistory && multiHistory && (
                      <TraceHistoryPlayBackControls
                        selectedTraceIndex={globalTraceIndex}
                        handleTraceIndexChange={handleTraceIndexChange}
                        traceHistoryLength={smallestTraceHistoryLength}
                        handleBackwardClicked={handleBackwardClicked}
                        handlePlayClicked={handlePlayClicked}
                        handleForwardClicked={handleForwardClicked}
                        handlePauseClicked={handlePauseClicked}
                        playTimerStatus={playTimerStatus}
                        playTimerInterval={playTimerInterval}
                        setPlayTimerInterval={setPlayTimerInterval}
                      />
                    )}
                  </Grid>
                  <Grid
                    container
                    item
                    xs={showMarkerHistory ? 6 : 12}
                    style={{ height: '100%' }}
                    justifyContent='center'
                  >
                    {traceHistory[1].length > 0 && (
                      <Grid item xs={graphSize}>
                        <TraceHistoryPlayBack
                          filter={{
                            analyzerId: watch1('savedAnalyzerId'),
                            snapshotId: watch1('savedSnapshotId'),
                          }}
                          traceHistory={traceHistory[1]}
                          traceCount={traceCount[1]}
                          traceIndex={localTraceIndex[1]}
                          onHover={() => onSnapshotHover(1)}
                          handleTraceIndexChanged={(newValue) =>
                            handleLocalTraceIndexChanged(1, newValue)
                          }
                          hideDetectorNames={hideDetectorNames}
                          onDrawingModeChange={handleDrawingChange}
                        />
                      </Grid>
                    )}
                    {traceHistory[2].length > 0 && (
                      <Grid item xs={graphSize}>
                        <TraceHistoryPlayBack
                          filter={{
                            analyzerId: watch2('savedAnalyzerId'),
                            snapshotId: watch2('savedSnapshotId'),
                          }}
                          traceHistory={traceHistory[2]}
                          traceCount={traceCount[2]}
                          traceIndex={localTraceIndex[2]}
                          onHover={() => onSnapshotHover(2)}
                          handleTraceIndexChanged={(newValue) =>
                            handleLocalTraceIndexChanged(2, newValue)
                          }
                          hideDetectorNames={hideDetectorNames}
                          onDrawingModeChange={handleDrawingChange}
                        />
                      </Grid>
                    )}
                  </Grid>

                  {showMarkerHistory && (
                    <Grid
                      item
                      xs
                      style={{ marginRight: '.2rem', marginLeft: '.5rem' }}
                    >
                      <MarkerHistory
                        traces={traceHistory[currentHoveredSnapshotKey]}
                        snapshotId={activeHistory.snapshotId}
                        moveTraceIndexById={moveTraceIndexById}
                        selectedTraceId={
                          traceHistory[currentHoveredSnapshotKey][
                            localTraceIndex[currentHoveredSnapshotKey]
                          ].id
                        }
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid item xs container style={{ position: 'relative' }}>
                  <Drawer
                    anchor='right'
                    variant='persistent'
                    open={drawerOpen}
                    sx={{
                      position: 'relative',
                      marginLeft: 'auto',
                      width: '100%',
                      '& .MuiBackdrop-root': {
                        display: 'none',
                      },
                      '& .MuiDrawer-paper': {
                        width: '100%',
                        position: 'absolute',
                        height: drawerHeight,
                        borderRadius: '10px',
                        border: 0,
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark' ? '#2d3640' : '#fff',
                      },
                    }}
                    transitionDuration={0}
                  >
                    {traceCount[1] > 0 && (
                      <SettingsTabPanel
                        selectedAnalyzerId={activeHistory.analyzerId}
                        selectedSnapshot={traceHistory[activeSettingsKey][0]}
                        selectedSnapshotId={activeHistory.snapshotId}
                        displayActiveSnapshotSelector={false}
                        editSnapshotsDisabled={true}
                        editMarkersDisabled={false}
                        isHistoricalMode
                        traceMode={0}
                        currentTimestamp={
                          multiHistory
                            ? traceHistory[currentHoveredSnapshotKey][0].time
                            : traceHistory[1][0].time
                        }
                      />
                    )}
                  </Drawer>
                  <IconButton
                    sx={{
                      position: 'absolute',
                      right: 10,
                      top: 0,
                      zIndex: 9999,
                    }}
                    onClick={() => setDrawerOpen((state) => !state)}
                  >
                    {drawerOpen ? <ChevronRight /> : <ChevronLeft />}
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid item />{' '}
        <ShareLinkDialog
          open={linkDialogOpen}
          setOpen={setLinkDialogOpen}
          link={linkUrl}
        />
      </Grid>
    </>
  );
};

export default TraceHistoryPage;
